import * as Sentry from '@sentry/react';
import { Action, createStore, combineReducers, applyMiddleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { persistStore } from 'redux-persist';
import { PersistPartial } from 'redux-persist/lib/persistReducer';
import thunk, { ThunkAction, ThunkDispatch, ThunkMiddleware } from 'redux-thunk';

import accessLog, { AccessLogState } from 'reducers/accessLog';
import addresses, { AddressesState } from 'reducers/addresses';
import adminUser, { AdminUserState } from 'reducers/adminUser';
import airports, { AirportsState } from 'reducers/airports';
import autos, { AutosState } from 'reducers/autos';
import cities, { CitiesState } from 'reducers/cities';
import companyBilling, { CompanyBillingState } from 'reducers/companyBilling';
import companyConfiguration, { CompanyConfigurationState } from 'reducers/companyConfiguration';
import costCenterReport, { CostCenterReportState } from 'reducers/costCenterReport';
import flights, { FlightsState } from 'reducers/flights';
import group, { GroupState } from 'reducers/group';
import hotelLocations, { HotelLocationsState } from 'reducers/hotelLocations';
import hotels, { HotelsState } from 'reducers/hotels';
import i18n, { I18nState } from 'reducers/i18n';
import pendingTravels, { PendingTravelState } from 'reducers/pendingTravels';
import report, { ReportState } from 'reducers/reports';
import travel, { TravelState } from 'reducers/travel';
import travelArrangerGuest, { TravelArrangerGuestState } from 'reducers/travelArrangerGuest';
import user, { UserState } from 'reducers/user';
import userLoyalties, { UserLoyaltiesState } from 'reducers/userLoyalty';
import toggle, { ToggleState } from 'reducers/eventsBooking';
import citiesAutoComplete, { CitiesAutoCompleteState } from 'reducers/citiesAutoComplete';
import countriesAutoComplete, { CountriesAutoCompleteState } from 'reducers/countriesAutoComplete';
import provincesAutoComplete, { ProvincesAutoCompleteState } from 'reducers/provincesAutoComplete';

export interface RootState {
  group: GroupState;
  i18n: I18nState;
  adminUser: AdminUserState;
  report: ReportState;
  airports: AirportsState & PersistPartial;
  cities: CitiesState & PersistPartial;
  citiesAutoComplete: CitiesAutoCompleteState & PersistPartial;
  provincesAutoComplete: ProvincesAutoCompleteState & PersistPartial;
  countriesAutoComplete: CountriesAutoCompleteState & PersistPartial;
  flights: FlightsState & PersistPartial;
  hotels: HotelsState & PersistPartial;
  autos: AutosState & PersistPartial;
  user: UserState & PersistPartial;
  travel: TravelState & PersistPartial;
  accessLog: AccessLogState & PersistPartial;
  pendingTravels: PendingTravelState & PersistPartial;
  companyBilling: CompanyBillingState & PersistPartial;
  addresses: AddressesState & PersistPartial;
  travelArrangerGuest: TravelArrangerGuestState;
  costCenterReport: CostCenterReportState;
  hotelLocations: HotelLocationsState & PersistPartial;
  companyConfiguration: CompanyConfigurationState & PersistPartial;
  userLoyalties: UserLoyaltiesState & PersistPartial;
  toggle: ToggleState & PersistPartial;
  
}

export type MyThunkResult<R> = ThunkAction<R, RootState, Record<string, never>, Action>;
export type MyThunkDispatch = ThunkDispatch<RootState, Record<string, never>, Action>;

const sentryEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state: RootState) => {
    const words = [
      'password',
      'secret',
      'passwd',
      'api_key',
      'apikey',
      'access_token',
      'auth_token',
      'credentials',
      'mysql_pwd',
      'stripetoken',
      'card',
      'cardNumber',
      'creditCard',
      'locations',
    ];

    const stateSensitive = Object.fromEntries(
      Object.entries(state)
        .filter(([reducerName]) => {
          if (words.some((word) => word.includes(reducerName))) return false;
          return true;
        })
        .map(([reducerName, reducerValue]) => [
          reducerName,
          Object.fromEntries(
            Object.entries(reducerValue).filter(([key, value]) => {
              if (words.some((word) => word.includes(key))) return false;
              if (value && words.some((word) => JSON.stringify(value).includes(word))) return false;
              return true;
            }),
          ),
        ]),
    );

    return stateSensitive;
  },
});

const rootReducer = combineReducers<RootState>({
  group,
  i18n,
  airports,
  adminUser,
  flights,
  hotels,
  autos,
  cities,
  citiesAutoComplete,
  provincesAutoComplete,
  countriesAutoComplete,
  user,
  report,
  travel,
  accessLog,
  pendingTravels,
  companyBilling,
  addresses,
  travelArrangerGuest,
  costCenterReport,
  hotelLocations,
  companyConfiguration,
  userLoyalties,
  toggle
});

export const store = createStore(
  rootReducer,
  composeWithDevTools(applyMiddleware(thunk as ThunkMiddleware), sentryEnhancer),
);

export const persistor = persistStore(store);
